import { useEffect, useRef, useState } from 'react';
import { getShopifyOrder } from './api';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 30000;

// Pull the JSON:API attributes bag off a `get('/v3/shopify_orders/:id')` response.
// Mirrors the shape already relied on in src/babys-first-year/index.js
// (`shopifyOrdersData?.initial?.data?.attributes?.[...]`).
const orderAttributes = (res) => res?.data?.initial?.data?.attributes || null;

// Polls `GET /v3/shopify_orders/:orderId` roughly every second, for up to ~30s,
// until `entitlements-count` is > 0 (a 410 is treated identically to
// `entitlements-count: 0` — "not ready yet", keep polling).
//
// Returned `status`:
//   'polling'    — still waiting on entitlements
//   'ready'      — entitlements exist, onboarding-state is 'unclaimed' or
//                  'in_progress' (both proceed through the same onboarding UI —
//                  the unclaimed/in_progress distinction is server-side
//                  bookkeeping about whether an anonymous member already holds
//                  the purchase, not a different client branch)
//   'claimed'    — entitlements exist but the order already belongs to a real
//                  account; caller should redirect to sign-in instead of
//                  onboarding
//   'timed_out'  — ~30s elapsed with entitlements-count still 0
export const usePollShopifyOrder = (orderId) => {
  const [state, setState] = useState({
    status: 'polling',
    order: null,
    onboardingState: null,
    error: null,
  });

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!orderId) {
      setState((s) => ({ ...s, status: 'timed_out' }));
      return undefined;
    }

    stoppedRef.current = false;

    const clearTimers = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      intervalRef.current = null;
      timeoutRef.current = null;
    };

    const checkOrder = async () => {
      if (stoppedRef.current) return;

      try {
        const res = await getShopifyOrder(orderId);
        const attributes = orderAttributes(res) || {};
        const entitlementsCount = Number(attributes['entitlements-count']) || 0;
        // May live on the order response itself; fall back gracefully if absent
        // (e.g. re-checked on a later poll, or simply not provided yet).
        const onboardingState = attributes['onboarding-state'] || null;

        if (entitlementsCount > 0) {
          stoppedRef.current = true;
          clearTimers();
          setState({
            status: onboardingState === 'claimed' ? 'claimed' : 'ready',
            order: attributes,
            onboardingState,
            error: null,
          });
        }
        // else entitlements-count is still 0 — keep polling silently.
      } catch (err) {
        // 410 == "not yet available" — identical to entitlements-count: 0.
        if (err?.response?.status === 410) return;
        // Any other transient error: don't abort the whole flow over one flaky
        // request, just remember it in case we eventually time out.
        setState((s) => ({ ...s, error: err }));
      }
    };

    checkOrder();
    intervalRef.current = setInterval(checkOrder, POLL_INTERVAL_MS);
    timeoutRef.current = setTimeout(() => {
      if (stoppedRef.current) return;
      stoppedRef.current = true;
      clearTimers();
      setState((s) => ({ ...s, status: 'timed_out' }));
    }, POLL_TIMEOUT_MS);

    return () => {
      stoppedRef.current = true;
      clearTimers();
    };
  }, [orderId]);

  return state;
};
